<template>
  <div>
    <div
      v-if="!$store.getters.getDashboardV2"
      id="app"
      :class="{
        'in-embed': isEmbed,
        'hide-logo': hideLogo,
        'login-page':
          $router.history.current.path &&
          $router.history.current.path.indexOf('/login/') === 0,
        'is-agent': isAgent,
      }"
      @click="handleClickApp($event)"
    >
      <!-- https://github.com/vuejs/vue-router/issues/1490, https://jsfiddle.net/Linusborg/L7hscd8h/1528/ -->
      <router-view :key="$route.fullPath"></router-view>

      <app-footer class="footer-panel" v-if="!isEmbed"></app-footer>
      <ErrorDialog />
      <ResultDialog />
      <ConfirmDialog />
      <InfoDialog />
      <InputDialog />
      <FilePreviewDialog />
      <SpinnerLoading v-if="isLoading" />
      <vue-simple-context-menu
        elementId="context-menu"
        :options="$store.getters.contextMenu.options"
        ref="contextMenu"
        @option-clicked="contextMenuOptionClicked"
      />
    </div>
  </div>
</template>

<script>
import Footer from "./components/global/Footer.vue";

import axios from "axios";
import router from "./router";

import ErrorDialog from "./components/common/global-dialogs/ErrorDialog";
import ConfirmDialog from "./components/common/global-dialogs/ConfirmDialog";
import ResultDialog from "./components/common/global-dialogs/ResultDialog";
import InfoDialog from "./components/common/global-dialogs/InfoDialog";
import InputDialog from "./components/common/global-dialogs/InputDialog";
import FilePreviewDialog from "./components/common/global-dialogs/FilePreviewDialog.vue";
import SpinnerLoading from "./components/common/global-dialogs/SpinnerLoading";
import { ADMIN_MENU_ALL_ITEMS } from "./constants/menu-items";
import {
  PARAM_USER_CREATED,
  PARAM_RESOURCE_INFO_KEY,
  VALUE_LOGIN,
  PARAM_LOGIN,
  PARAM_BACKGROUND_KEY,
  PARAM_HIDE_ERROR_MODAL,
  PARAM_HIDE_LOADING,
} from "./constants/url-params";

import { MULTI_LOGIN_ERROR_TEXT } from "./constants/default-values";

import { BUILDER_USER_TYPE } from "./constants/custom-define-type";
import { ERROR_CODE } from "./constants/error-code";
import "vue-simple-context-menu/dist/vue-simple-context-menu.css";
import VueSimpleContextMenu from "vue-simple-context-menu";

import { TOP_MENU_ALL_ITEMS, LIST_ALL_MENU_IDS } from "./constants/menu-items";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "app",
  data() {
    return {
      msg: "Welcome to Your Vue.js App",
      map: null,
      selector: null,
      showMobileNav: false,
      isLoadingEmployerList: false,
      isLoadingCandidateList: false,
      isLoadingAdminList: false,
      isLoadingProfileTemplateList: false,
      isLoadingCandidateProfileTemplateList: false,
      isLoadingClientProfileTemplateList: false,
      isLoadingAgency: false,
      isLoadingPackageList: false,
      TOP_MENU_ALL_ITEMS,
      ADMIN_MENU_ALL_ITEMS,
      selectedMenuItem: "Dashboard",
      intervalFunc: null,
      checkMultiLoginInvervalFunc: null,
      hideLogo: false,
      showEngineHow: true,
      showRecentViewed: true,
    };
  },
  created() {
    if (this.$route.query && this.$route.query["hide-logo"]) {
      this.hideLogo = true;
    }
    this.setupInterceptor();
    // Not try to login in case of called inside iframe or in application form
    if (this.isAutoLoginNeeded) {
      try {
        this.$store.dispatch("tryAutoLogin");
      } catch (e) {
        // try to login in may has exception if cannot access localStorage
        console.log(e);
      }
    }

    this.$store.watch(
      (state) => state.authentication.agencyID,
      (value) => {
        // Agency changed
        this.$store.dispatch("setProfileBuilderList", null);
        this.$store.dispatch("setProfileTemplateList", []);
        this.$store.dispatch("setCandidateList", []);
        this.$store.dispatch("setEmployerList", []);
        this.$store.dispatch("setAgency", {});
        this.$store.dispatch("setAdminSetting", {});

        // Set timeout to wait for the agency API request
        if (value) {
          setTimeout(() => {
            this.getPublicAgency();
          }, 100);
        }
      },
      { immediate: true }
    );

    this.setColor();
    this.setFonts();
    this.setDataList();
    this.setContextMenu();

    // Get number of messages
    this.intervalFunc = setInterval(async () => {
      this.loadMessagesSummary();
    }, 120000);
    if (this.$route.query.selector === "Home") {
      this.showEngineHow = false;
      this.showRecentViewed = false;
    }
  },
  watch: {
    $route: {
      handler(to, from) {
        if (to) {
          if (
            [
              "Agent Dashboard",
              "Employer Dashboard",
              "Candidate Dashboard",
            ].includes(to.name)
          ) {
            this.selectedMenuItem = "Dashboard";
          } else {
            this.selectedMenuItem = to.name;
          }
        }
        if (to.path == "/dashboard-v2") {
          this.$store.commit("setDashboardV2", true);
        } else {
          this.$store.commit("setDashboardV2", false);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.setWindowWidth(window.innerWidth);
    document.addEventListener("click", this.onClick);
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("message", this.receiveMessage);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onClick);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("message", this.receiveMessage);
    if (this.intervalFunc) {
      clearInterval(this.intervalFunc);
    }
    if (this.checkMultiLoginInvervalFunc) {
      clearInterval(this.checkMultiLoginInvervalFunc);
    }
    if (this.checkRecentSMSMessagesFunc) {
      clearInterval(this.checkRecentSMSMessagesFunc);
    }
  },
  components: {
    ErrorDialog,
    ConfirmDialog,
    ResultDialog,
    InfoDialog,
    InputDialog,
    FilePreviewDialog,
    AppFooter: Footer,
    VueSimpleContextMenu,
    SpinnerLoading,
  },
  computed: {
    ...mapGetters([
      "employerList",
      "candidateList",
      "employerLastLoginFilter",
      "candidateLastLoginFilter",
      "hasMessage",
      "isAgent",
      "loggedinUser",
      "jobTypes",
      "showShiftJobBoardInternally",
      "shiftJobViewingStatuses",
      "shiftJobViewingTags",
      "shiftJobViewingTypes",
      "placementJobViewingStatuses",
      "placementJobViewingTypes",
      "hideMenuBar",
    ]),
    // Check if auto login needed
    isAutoLoginNeeded() {
      // Not auto login if in an embeded site
      if (this.isEmbed) {
        return false;
      }
      if (
        this.$route &&
        [
          "Application Form Builder",
          "Login",
          "Agency Job Board",
          "Job",
          "Public Job",
        ].includes(this.$route.name)
      ) {
        // Get agency id in localstorage
        let storedAgencyID;
        try {
          storedAgencyID = localStorage.getItem("agency_id");
        } catch (e) {
          // Cannot read localstorage, don't need auto login
          return false;
        }
        // Auto login if the login user access to profile builder of same agency
        return Number(storedAgencyID) === Number(this.$route.params.agencyID);
      }
      return true;
    },
    auth() {
      // Lecture 363
      return this.$store.getters.isAuthenticated;
    },
    title: {
      get() {
        document.title = this.remaining;
        return this.remaining;
      },
      set(val) {
        // console.log(val)
        document.title = val;
      },
    },
    isLoading() {
      return this.$store.getters.loadingReqNumber > 0;
    },
    /* Check if the app is displayed inside an iframe or embed tag
    https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t
    */
    isEmbed() {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    },
  },
  methods: {
    ...mapActions([
      "loadMessagesSummary",
      "getListRecentSMSLogs",
      "setWindowWidth",
      "setLoggedinUser",
      "setIframePositionTop",
      "setWindowHeight",
      "setIframeScaleY",
      "setIframeParentHeight"
    ]),
    receiveMessage(event) {
      try {
        const eventData = JSON.parse(event.data);
        if (eventData.iframePositionTop != null) {
          this.setIframePositionTop(eventData.iframePositionTop);
        }
        if (eventData.windowHeight != null) {
          this.setWindowHeight(eventData.windowHeight);
        }
        if (eventData.windowHeight != null) {
          this.setIframeParentHeight(eventData.windowHeight);
        }
        if (eventData.scaleY) {
          this.setIframeScaleY(eventData.scaleY);
        }
      } catch (e) {}
    },
    setDataList() {
      this.$store.watch(
        (state) => state.general.loadPackageListReqNumber,
        (val) => {
          if (!val) {
            return;
          }
          // Check if package list is loaded
          if (this.$store.getters.packageList) {
            return;
          }
          this.getPackageList();
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.general.loadProfileTemplateListReqNumber,
        (val) => {
          if (!val) {
            return;
          }
          // Check if profile template list is loaded
          if (
            this.$store.getters.profileTemplateList &&
            this.$store.getters.profileTemplateList.length > 0
          ) {
            return;
          }
          this.getProfileTemplateList();
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.general.reloadProfileTemplateListReqNumber,
        (val) => {
          if (!val) {
            return;
          }
          this.getProfileTemplateList();
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.general.loadCandidateProfileTemplateListReqNumber,
        (val) => {
          if (!val) {
            return;
          }
          // Check if candidate builder list is loaded
          if (
            this.$store.getters.candidateProfileTemplateList &&
            this.$store.getters.candidateProfileTemplateList.length > 0
          ) {
            return;
          }
          this.getCandidateProfileTemplateList();
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.general.loadClientProfileTemplateListReqNumber,
        (val) => {
          if (!val) {
            return;
          }
          // Check if client builder list is loaded
          if (
            this.$store.getters.clientProfileTemplateList &&
            this.$store.getters.clientProfileTemplateList.length > 0
          ) {
            return;
          }
          this.getClientProfileTemplateList();
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.agency.loadAgencyReqNumber,
        (val) => {
          if (!val) {
            return;
          }
          // Check if agency is loaded
          if (
            this.$store.getters.agency &&
            Object.keys(this.$store.getters.agency).length > 0
          ) {
            return;
          }
          this.getAgency();
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.agency.reloadAgencyReqNumber,
        (val) => {
          if (!val) {
            return;
          }
          this.getAgency();
        },
        { immediate: true }
      );
    },
    setColor() {
      this.$store.watch(
        (state) => state.colors.primaryColor,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--coredarkcolor", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.colors.primaryDarkerColor,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--coredarkercolor", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.colors.secondaryColor,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--corelightcolor", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.colors.secondaryDarkerColor,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--thrivehiregrey", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.colors.profileTextColor,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--profiletextcolor", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.colors.cancelButtonColor,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--cancelbuttoncolor", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.colors.cancelButtonHoverColor,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--cancelbuttonhovercolor", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.colors.labelColor,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--labelcolor", value);
        },
        { immediate: true }
      );
    },
    setFonts() {
      this.$store.watch(
        (state) => state.fonts.rootFont,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--rootfontfamily", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.fonts.headingsFont,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--headingsfontfamily", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.fonts.headingOneCase,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--headingOneCase", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.fonts.headingTwoCase,
        (value) => {
          if (!value) {
            return;
          }
          console.log("headingTwoCase", value);
          const selector = document.querySelector(":root");
          selector.style.setProperty("--headingTwoCase", value);
        },
        { immediate: true }
      );
      this.$store.watch(
        (state) => state.fonts.headingThreeCase,
        (value) => {
          if (!value) {
            return;
          }
          const selector = document.querySelector(":root");
          selector.style.setProperty("--headingThreeCase", value);
        },
        { immediate: true }
      );
    },
    setContextMenu() {
      this.$store.watch(
        (state) => state.general.contextMenu,
        (value) => {
          if (!value || !this.$refs.contextMenu) {
            return;
          }
          this.$nextTick(() => {
            this.$refs.contextMenu.showMenu(value.event);
          });
        },
        { immediate: true }
      );
    },
    notifyDurationTime(config) {
      try {
        const duration = new Date().getTime() - config.meta.requestStartedAt;
        if (duration > 30000) {
          this.$bugsnag.notify(
            new Error(
              `Long response time ${this.$store.getters.agencyID} ${duration} ${config.url}`
            ),
            {
              metaData: {
                url: config.url,
                agencyId: this.$store.getters.agencyID,
                duration: duration,
              },
            }
          );
        }
      } catch (e) {
        console.log(e);
      }
    },
    setupInterceptor() {
      axios.interceptors.request.use(
        (config) => {
          try {
            if (localStorage.getItem("token")) {
              config.headers["Authorization"] =
                "Token " + localStorage.getItem("token");
            }
          } catch (e) {
            // Cannot access to localstorage in case of access from iframe or some case in Safari of iPhone
            // Refer to https://stackoverflow.com/questions/46632093/getting-security-error-on-iphone-when-using-localstorage
            if (this.$store.getters.userToken) {
              config.headers["Authorization"] =
                "Token " + this.$store.getters.userToken;
            }
          }
          try {
            config.meta = config.meta || {};
            config.meta.requestStartedAt = new Date().getTime();
          } catch (e) {}
          // https://stackoverflow.com/questions/48173717/enabling-cors-on-a-nginx-server-in-elasticbeanstalk/48721443
          config.headers["Access-Control-Allow-Origin"] = "*";

          // Check to add trailing slash to url
          let url = config.url.trim();
          const tokens = url.split("?");
          if (tokens[0].includes("/api/")) {
            if (url.includes("?")) {
              if (!tokens[0].endsWith("/")) {
                url = url.replace("?", "/?");
              }
            } else {
              // Check trailing slash
              if (!url.endsWith("/")) {
                url = url + "/";
              }
            }
            config.url = url;
          }
          // Add timezone to request body of Job API
          const listEndpointAddUrlPrefix = [
            "/api/jobs/",
            "/job-fillers",
            "/api/actions/",
            "/send-and-log-email/",
            "/api/shiftjobreports/",
            "/application-submit/",
            "/api/comments/",
            "/api/questions/",
            "/send-repeat-blaster/",
            "/set-user-status/",
            "/api/events/",
          ];
          if (
            listEndpointAddUrlPrefix.some((prefix) => url.indexOf(prefix) === 0)
          ) {
            if (config.data && !(config.data instanceof FormData)) {
              config.data = {
                ...config.data,
                timezone:
                  config.data.timezone ||
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
                root_url: this.rootUrl,
              };
            }
          }
          if (!this.isHideLoadingRequest(url)) {
            this.showLoading();
          }

          return config;
        },
        (error) => {
          console.log(error);
          this.hideLoading();
          return Promise.reject(error);
        }
      );
      axios.interceptors.response.use(
        (response) => {
          let url;
          if (response && response.config) {
            url = response.config.url;
            this.notifyDurationTime(response.config);
          }
          if (!this.isHideLoadingRequest(url)) {
            this.hideLoading();
          }
          return response;
        },
        async (error) => {
          let url;
          if (error && error.config) {
            url = error.config.url;
            this.notifyDurationTime(error.config);
          }
          if (this.isBackgroundRequest(url)) {
            return error;
          }
          if (!this.isHideLoadingRequest(url)) {
            this.hideLoading();
          }

          if (
            error.response &&
            error.response.status == 409 &&
            error.response.data &&
            error.response.data.multi_login_access
          ) {
            return error;
          }

          if (!this.hideErrorModal(url)) {
            if (
              !this.isAgent &&
              error.response &&
              error.response.status == 409 &&
              error.response.data &&
              error.response.data.timeoff_assigned_job_conflict
            ) {
              return error;
            }

            this.handleError(error);
          }
        }
      );
    },
    getWarningUserCreatedResource(error) {
      if (error == null || error.response == null) {
        return null;
      }
      if (error.response.config == null || error.response.config.url == null) {
        return null;
      }
      const url = error.response.config.url;
      // Check for login API
      if (url.includes(PARAM_LOGIN)) {
        return VALUE_LOGIN;
      }
      if (!url.includes(PARAM_USER_CREATED)) {
        return null;
      }
      return this.getParameterByName(url, PARAM_RESOURCE_INFO_KEY);
    },
    isBackgroundRequest(url) {
      if (!url) {
        return false;
      }
      return this.getParameterByName(url, PARAM_BACKGROUND_KEY) === "true";
    },
    isHideLoadingRequest(url) {
      if (!url) {
        return false;
      }
      if (this.isBackgroundRequest(url)) {
        return true;
      }
      return this.getParameterByName(url, PARAM_HIDE_LOADING) === "true";
    },
    hideErrorModal(url) {
      if (!url) {
        return false;
      }
      return this.getParameterByName(url, PARAM_HIDE_ERROR_MODAL) === "true";
    },
    warningConflict(error) {
      const responseData = error.response.data;
      let detailMessage = responseData.message || "";
      detailMessage = detailMessage
        .replace("Please select another candidate", "")
        .trim();
      return new Promise((resolve) => {
        this.showConfirmDialog({
          innerHtml: `<h2>Do you want to continue and ignore the conflict?<h2><h5>${detailMessage}</h5>`,
          onAcceptCallback: () => {
            let requestBody = error.config.data;
            try {
              requestBody = JSON.parse(requestBody);
            } catch (e) {}
            if (responseData.availability_conflict) {
              requestBody.ignore_availability_conflict = true;
            }
            if (responseData.time_conflict) {
              requestBody.ignore_time_conflict = true;
            }
            if (responseData.unique_value_conflict) {
              requestBody.ignore_unique_value_conflict = true;
            }
            if (responseData.timeoff_assigned_job_conflict) {
              requestBody.ignore_timeoff_assigned_job_conflict = true;
            }
            if (responseData.status_conflict) {
              requestBody.check_job_status = false;
            }
            if (responseData.do_not_match) {
              requestBody.ignore_do_not_match = true;
            }
            const res = axios.request({
              ...error.config,
              data: requestBody,
            });
            resolve(res);
          },
          onRejectCallback: () => {
            resolve();
          },
        });
      });
    },
    async handleError(error) {
      const warningResource = this.getWarningUserCreatedResource(error);
      if (error == null || error.response == null) {
        if (warningResource) {
          this.showErrorDialog({
            title: "Your application has been submitted! BUT",
            message: `There was an error processing your ${warningResource} (Error: ${String(
              error
            )}). Could you please login and update in your profile.`,
          });
        } else {
          let errorMessage = String(error);
          if (errorMessage === "Error: Network Error") {
            this.showErrorDialog("NETWORK_ERROR");
          } else {
            this.showErrorDialog({
              title: "There is an error",
              message: errorMessage,
            });
          }
        }
      } else {
        if (error.response.data) {
          const errorCode = error.response.data.error_code;
          if (errorCode === ERROR_CODE.USER_EXISTED) {
            this.showConfirmDialog({
              innerHtml:
                this.$store.getters.agencyPublic.duplicate_email_error_message,
              acceptLabel: "Navigate to Login",
              rejectLabel: "Edit the form",
              onAcceptCallback: () => router.replace({ name: "Login" }),
            });
            return;
          }
          const detail = error.response.data.detail;
          if (
            this.$store.getters.userID &&
            detail === "Sorry, you no longer have profile access."
          ) {
            // Reload user
            const res = await axios.get(
              `/api/people/${this.$store.getters.userID}/`
            );
            this.setLoggedinUser(res.data);
          }
        }
        if (warningResource) {
          if (warningResource === VALUE_LOGIN) {
            if (error.response.status === 400) {
              this.showErrorDialog({
                title: "Incorrect email or password",
                message:
                  "Sorry, your email or password is incorrect! Please try again, or use the forgot password link to rest your password.",
              });
            } else {
              this.showErrorDialog({
                title: "There is an error",
                errorObj: error.response.data,
              });
            }
            return;
          }
          this.showErrorDialog({
            title: "Your application has been submitted! BUT",
            message: `There was an error processing your ${warningResource}. Could you please login and update in your profile.`,
            errorObj: error.response.data,
          });
        } else {
          this.showErrorDialog({
            title: "There is an error",
            errorObj: error.response.data,
          });
        }
      }

      throw error;
    },
    onClick() {
      // Adding event handler to document:
      // https://stackoverflow.com/questions/41950432/vue-js-call-the-click-event-for-whole-page-document
      if (this.showMobileNav === true) {
        this.showMobileNav = false;
      }
    },
    logout(keepCache) {
      if (keepCache != true) {
        this.removeCheckMultipleLoginCache();
      }
      this.$store.dispatch("logout");
    },
    checkMultipleLogin(forceLogout) {
      // check multiple logins and if it is not multiple login, update the access time
      if (localStorage.getItem("token")) {
        axios
          .post(`check-multiple-login/?resource=login&in_background=true`, {})
          .then((res) => {
            if (res.response && res.response.status == 409) {
              if (forceLogout) {
                this.logout(true);
                this.showErrorDialog("FORCE_LOGOUT_ERROR_DIALOG");
              }

              if (this.checkMultiLoginInvervalFunc) {
                clearInterval(this.checkMultiLoginInvervalFunc);
                this.checkMultiLoginInvervalFunc = null;
              }
            }
          });
      }
    },
    removeCheckMultipleLoginCache() {
      if (localStorage.getItem("token")) {
        axios.delete(`check-multiple-login/?in_background=true`, {
          headers: {
            Authorization: "Token " + localStorage.getItem("token"),
          },
        });
      }
    },
    async getPublicAgency() {
      if (!this.$store.getters.agencyID) {
        return;
      }
      // If agency is loaded or loading, don't need to load agenciespublic
      if (this.isLoadingAgency) {
        return;
      }
      if (
        this.$store.getters.agency &&
        Array.keys(this.$store.getters.agency).length
      ) {
        return;
      }
      const res = await axios.get(
        `/api/agenciespublic/${this.$store.getters.agencyID}/`
      );
      this.setPublicAgency(res.data);
    },
    // Get profile template list for both client and candidate
    async getProfileTemplateList() {
      if (!this.$store.getters.agencyID) {
        return;
      }
      // If there is one request sending, skip current request
      if (this.isLoadingProfileTemplateList) {
        return;
      }
      // Mark there is one request sending
      this.isLoadingProfileTemplateList = true;

      const res = await axios.get(
        "/api/profiletemplates/?agency=" + this.$store.getters.agencyID
      );
      setTimeout(() => {
        this.$store.dispatch("setIsLoadingProfileTemplate", false);
      }, 100);

      this.isLoadingProfileTemplateList = false;
      this.$store.dispatch("setProfileTemplateList", res.data ? res.data : []);
    },
    // Get profile template list for candidate
    async getCandidateProfileTemplateList() {
      if (!this.$store.getters.agencyID) {
        return;
      }
      // If profile template list is loading, skip current request
      if (this.isLoadingProfileTemplateList) {
        return;
      }
      // If there is one request sending, skip current request
      if (this.isLoadingCandidateProfileTemplateList) {
        return;
      }
      // Mark there is one request sending
      this.isLoadingCandidateProfileTemplateList = true;

      const res = await axios.get(
        `/api/profiletemplates/?agency=${this.$store.getters.agencyID}&user_type=${BUILDER_USER_TYPE.CANDIDATE}`
      );
      setTimeout(() => {
        this.$store.dispatch("setIsLoadingProfileTemplate", false);
      }, 100);

      this.isLoadingCandidateProfileTemplateList = false;
      this.$store.dispatch(
        "setCandidateProfileTemplateList",
        res.data
          ? res.data.filter((template) => template.deactivated === false)
          : []
      );
    },
    // Get profile template list for client
    async getClientProfileTemplateList() {
      if (!this.$store.getters.agencyID) {
        return;
      }
      // If profile template list is loading, skip current request
      if (this.isLoadingProfileTemplateList) {
        return;
      }
      // If there is one request sending, skip current request
      if (this.isLoadingClientProfileTemplateList) {
        return;
      }
      // Mark there is one request sending
      this.isLoadingClientProfileTemplateList = true;

      const res = await axios.get(
        `/api/profiletemplates/?agency=${this.$store.getters.agencyID}&user_type=${BUILDER_USER_TYPE.CLIENT}`
      );
      setTimeout(() => {
        this.$store.dispatch("setIsLoadingProfileTemplate", false);
      }, 100);
      console.log("res 1 ========>", res);
      this.isLoadingClientProfileTemplateList = false;
      this.$store.dispatch(
        "setClientProfileTemplateList",
        res.data
          ? res.data.filter((template) => template.deactivated === false)
          : []
      );
      console.log("res 2 ========>", res);
    },
    async getPackageList() {
      if (!this.$store.getters.agencyID) {
        return;
      }
      // If there is one request sending, skip current request
      if (this.isLoadingPackageList) {
        return;
      }
      // Mark there is one request sending
      this.isLoadingPackageList = true;

      const res = await axios.get(
        `/api/packages/?agency=${this.$store.getters.agencyID}`
      );

      this.isLoadingPackageList = false;
      let packageList = res.data;
      if (!packageList) {
        packageList = [];
      }
      packageList = packageList.sort((a, b) => (a.order > b.order ? 1 : -1));
      this.$store.dispatch("setPackageList", packageList);
    },
    async getAgency() {
      if (!this.$store.getters.agencyID) {
        return;
      }
      // If there is one request sending, skip current request
      if (this.isLoadingAgency) {
        return;
      }
      // Mark there is one request sending
      this.isLoadingAgency = true;

      const res = await axios.get(
        `/api/agencies/${this.$store.getters.agencyID}`
      );
      let agency = {};
      if (res && res.data) {
        agency = res.data;
      }
      this.isLoadingAgency = false;
      this.setAgency(agency);
    },
    async checkRecentSMSMessages() {
      const listRecentLogs = await this.getListRecentSMSLogs();
      if (listRecentLogs && listRecentLogs.length) {
        for (const log of listRecentLogs) {
          let content = log.phone;
          if (log.user_name) {
            content = `${content} (${log.user_name})`;
          }
          content = `${content}: ${log.message}`;
          this.$toasted.show(content, {
            duration: 15000,
            action: [
              {
                icon: "close",
                class: "close-toast-btn",
                onClick: (e, toastObject) => {
                  toastObject.goAway(0);
                },
              },
            ],
          });
        }
      }
    },
    dashboardRedirect() {
      if (this.$store.getters.isCandidate) {
        router.replace({ name: "Candidate Dashboard" });
      } else if (this.$store.getters.isEmployer) {
        router.replace({ name: "Employer Dashboard" });
      } else if (this.$store.getters.isAgent) {
        router.replace({ name: "Agent Dashboard" });
      }
    },
    handleScroll() {
      this.$store.dispatch("setPageScrollTop", window.scrollY);
    },
    handleResize() {
      this.setWindowWidth(window.innerWidth);
      this.$store.dispatch("increaseScreenResizeCount");
    },
    handleClickApp(event) {
      this.$store.dispatch("setLastClickY", event.y);
    },
    contextMenuOptionClicked(event) {
      if (event && event.option) {
        if (event.option.onSelectData) {
          event.option.onSelect(event.option.onSelectData);
        } else {
          event.option.onSelect();
        }
      }
    },
    handleClickHelp() {
      this.submitTrackingClick("Click Enginehire Help", "Global Button");
    },
  },
};
</script>

<style>
@import "./assets/styles/search-setting.css";
@import "./assets/styles/table-style.css";
:root {
  --coredarkcolor: #1f4e78;
  --coredarkercolor: #183c5c;
  --corelightcolor: #fff;
  --bootstrapgrey: #e9ecef;
  --skyblue: #bfe7ff;
  --thrivehiregrey: #d9d9da;
  --nannycogreen: #0c4524;
  --gcnorange: #e79271;
  --gcnteal: #85a2a8;
  --rootfontfamily: "Open Sans";
  --headingsfontfamily: "Open Sans";
  --profiletextcolor: #212529;
  --cancelbuttoncolor: #5f7478;
  --cancelbuttonhovercolor: #85a2a8;
  touch-action: pan-x pan-y;
  height: 100%;
}

body {
  /* font-family: 'articulatcf-demibold', 'Montserrat', sans-serif; */
  font-family: var(--rootfontfamily) !important;
  /* font-size: 13px; */
  /* line-height: 1.42857143; */
  background-color: transparent;
}

h1,
h2,
h3,
h4,
h5 {
  font-family: var(--headingsfontfamily) !important;
}

#app {
  /* font-family: 'Avenir', Helvetica, Arial, sans-serif; */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /* text-align: center; */
  /* color: #2c3e50; */
  /* margin-top: 60px; */

  /* width: 100vw; */
  /* height: 100vh; */
}

.top-panel {
  padding: 0.5rem 2.5%;
}

.top-level-div {
  /* padding-bottom: 20vh;
  padding-top: 15vh; */
  padding-bottom: 125px;
  padding-left: 12.5%;
  padding-right: 12.5%;
}
.invert-top-level-div {
  margin-bottom: -125px;
}

.center {
  text-align: center;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

/* @font-face {
    font-family: "Articulat Demibold";
    src: url("assets/fonts/articulatcf-demiboldoblique-webfont.woff2") format("woff2");
  } */

h1 {
  /* font-family: 'articulatcf-demibold', 'Open Sans', 'Lato'; */
  color: var(--coredarkcolor);
  /* font-weight: 300; */
  text-transform: var(--headingOneCase) !important;
  font-weight: 500;
}

h2 {
  /* font-family: 'articulatcf-demibold', 'Open Sans'; */
  color: var(--coredarkcolor);
  text-transform: var(--headingTwoCase) !important;
  font-weight: 500;
}

h3 {
  /* font-family: 'articulatcf-demibold', 'Open Sans', 'Lato'; */
  /* color: var(--gcnorange); */
  color: var(--coredarkcolor);
  text-transform: var(--headingThreeCase) !important;
}

.heavy-font {
  font-weight: 600;
}

p {
  /* font-family: 'articulatcf-regular', 'Open Sans', 'Lato'; */
  /* font-family: 'Open Sans'; */
  margin-bottom: 0;
}

/* https://stackoverflow.com/questions/10270891/newline-in-models-textfield-not-rendered-in-template
  https://www.w3schools.com/tags/tag_pre.asp */
pre {
  font-size: 100%;
  /* overflow-x: hidden; */
  /* overflow-wrap: break-word; */
  /* Prevent side scrolling of long lines */
  white-space: pre-wrap; /* https://mediatemple.net/blog/tips/considerations-for-styling-the-pre-tag/ */
  margin-bottom: 0;

  font-family: var(--rootfontfamily) !important;
}
pre p {
  margin-bottom: 10px;
}
pre h3 {
  margin-top: 15px;
}
pre h4 {
  margin-top: 15px;
}
pre h5 {
  margin-top: 15px;
}
pre img {
  margin-top: 8px;
  margin-bottom: 8px;
}

small {
  /* font-family: 'articulatcf-regular', 'Lato'; */
}

ul {
  margin-bottom: 0;
}

li {
  /* font-family: 'articulatcf-regular', 'Open Sans', 'Lato'; */
}

a {
  /* font-family: 'articulatcf-regular', 'Open Sans', 'Lato'; */
}
a:hover {
  text-decoration: none;
}

.delete-text {
  color: red;
  cursor: pointer;
}
.edit-text {
  color: var(--coredarkcolor);
  cursor: pointer;
}
.warning-text {
  color: red;
  font-weight: 600;
}

.profile-page {
  color: var(--profiletextcolor);
}

/* --------- BUTTONS --------- */
.btn {
  border: none;
}

.btn-lg {
  font-size: 1.1rem;
}

.btn-primary {
  background-color: var(--coredarkcolor);
  font-weight: 600;
}
.btn-primary:hover {
  background-color: var(--thrivehiregrey);
  color: var(--coredarkcolor);
}
.btn-outline-primary {
  /* border: 1px solid #5289B9; */
  color: #5289b9;
}

.btn-secondary {
  /* background-color: #5289B9; */
  /* background-color: var(--corelightcolor); */
  background-color: var(--bootstrapgrey);
  color: var(--coredarkcolor);
  /* text-transform: uppercase; */
  /* font-family: 'Lato'; */
  /* font-weight: 700; */
}
.btn-secondary:hover {
  /* background-color: #49759c; */
  background-color: var(--thrivehiregrey);
  color: var(--coredarkcolor);
  /* font-weight: 700; */
}
.btn-outline-secondary {
  /* border: 1px solid #5289B9; */
  /* color: #5289B9; */
}

.btn-grey {
  background-color: var(--bootstrapgrey);
  color: var(--coredarkcolor);
}
.btn-grey:hover {
  background-color: var(--coredarkcolor);
  color: var(--corelightcolor);
}

/* This is styling copied over from mylisting */
.buttons {
  /* font-size: 13px; */
  /* padding: 14px 40px; */
  /* -webkit-transition: all .2s ease; */

  display: inline-block;
  transition: all 0.2s ease;
  overflow: hidden;
  border-radius: 3px;
  line-height: 16px;
  font-weight: 700;
}
/* The Add an Opportunity button top right */
.header-button {
  align-items: center;
  height: 50px;
  /* padding: 15px 25px; */
}
.header-button button {
  padding: 15px 25px;
}
/* .header-button router-link {
    padding: 15px 25px;
  } */

.button-1 {
  color: var(--coredarkcolor);
  border: none;
  background: #fff;
  font-size: 14px;
  /* font-weight: 700; */
}
.button-1:hover {
  color: #fff;
  cursor: pointer;
  background: var(--coredarkcolor);
}
.button-1-dark {
  color: var(--corelightcolor);
  border: none;
  background: var(--coredarkcolor);
  font-size: 14px;
}
.button-1-dark:hover {
  color: var(--coredarkcolor);
  cursor: pointer;
  background: var(--thrivehiregrey);
}

.landing-button {
  min-width: 165px;
  font-weight: 800;
  margin-top: 20px;
}

.mylisting-button {
  min-width: 135px;
  text-align: center;
  font-weight: 600;
  /* margin-top: 20px; */
  padding: 13px 25px;
  border-radius: 0px;
  font-size: 12px;
  /* height: 48px; */
  -webkit-box-align: center;
  align-items: center;
  position: relative;
  z-index: 0;
  -webkit-box-pack: center;
  justify-content: center;
  white-space: nowrap;
  border: 2px solid #0c4524;
  border: none;
  /* margin-top: 10px; */
  /* background-size: 200% 100%;
    background-position: right bottom;
    transition: all .25s ease-out; */
}

/* This is to get rid of underlining for router links used as buttons */
a.mylisting-button:hover {
  text-decoration: none;
}

a.mylisting-button {
  display: inline-block;
}

.dark-button {
  color: white;
  /* background-color: white; */
  /* Add a shadow over the search button left to right when you hover over it */
  /* https://codepen.io/kay8/pen/azKbjN */
  /* https://stackoverflow.com/questions/17212094/fill-background-color-left-to-right-css */
  background: linear-gradient(
    to right,
    var(--coredarkercolor) 50%,
    var(--coredarkcolor) 50%
  );
  background-size: 200% 100%;
  background-position: right bottom;
  transition: all 0.25s ease-out;
}
.dark-button:hover {
  color: #fff;
  background-position: left bottom;
  /* background-color: #0c4524; */
}

.secondary-button {
  color: #fff;
  /* color: var(--coredarkcolor); */
  background: linear-gradient(to right, #5f7478 50%, var(--gcnteal) 50%);
  background-size: 200% 100%;
  background-position: right bottom;
  transition: all 0.25s ease-out;
}
.secondary-button:hover {
  color: #fff;
  background-position: left bottom;
}

.notransition * {
  -webkit-transition: none !important;
  -moz-transition: none !important;
  -o-transition: none !important;
  -ms-transition: none !important;
  transition: none !important;
}
.noHover {
  pointer-events: none;
}

.danger-button {
  color: #fff;
  background: linear-gradient(to right, #cc0000 50%, red 50%);
  background-size: 200% 100%;
  background-position: right bottom;
  transition: all 0.25s ease-out;
}
.danger-button:hover {
  color: #fff;
  background-position: left bottom;
}

.primary-button {
  color: #fff;
  background: linear-gradient(to right, #007bff 50%, #2f00ff 50%);
  background-size: 200% 100%;
  background-position: right bottom;
  transition: all 0.25s ease-out;
}
.primary-button:hover {
  color: #fff;
  background-position: left bottom;
}

.light-button {
  color: var(--coredarkcolor);
  border: 2px solid var(--coredarkcolor);
  /* Add a shadow over the search button left to right when you hover over it */
  /* https://codepen.io/kay8/pen/azKbjN */
  /* https://stackoverflow.com/questions/17212094/fill-background-color-left-to-right-css */
  background: linear-gradient(to right, var(--coredarkcolor) 50%, #fff 50%);
  background-size: 200% 100%;
  background-position: right bottom;
  transition: all 0.25s ease-out;
}
.light-button:hover {
  /* color: var(--coredarkcolor); */
  color: #fff;
  background-position: left bottom;
}

.cancel-button {
  background: linear-gradient(
    to right,
    var(--cancelbuttonhovercolor) 50%,
    var(--cancelbuttoncolor) 50%
  );
  background-size: 200% 100%;
  background-position: right bottom;
}

.actions-btn {
  color: #007bff;
  padding: 0 4px;
}

/* --------------------- */

/* ------ NAVBAR ------- */
.nav-item {
  margin-right: 15px;
  font-size: 13px;
  color: var(--corelightcolor);
}

.dropleft {
  position: absolute;
  top: 4px;
  right: 0;
  margin-top: 4px;
  z-index: 9999;
}

.dropleft .dropdown-toggle::before {
  display: inline-block;
  margin-right: 0.255em;
  vertical-align: 0.255em;
  content: "";
  border-top: 0.3em solid transparent;
  border-right: 0.3em solid;
  border-bottom: 0.3em solid transparent;
  border: none;
}
/* --------------------- */

.footer {
  /* background-color: white;
    border-top: 1px solid #ead9cf;
    text-align: right;
    margin-bottom: 0;
    width: auto;
    width: 80%;
    max-width: 1280px;
    padding-top: 24px;
    padding-bottom: 24px; */
}

.footer p {
  margin-bottom: 0;
}

/* ------ USER PROFILES ------ */
.profile-tile {
  border-radius: 6px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  padding: 1.5rem;
  margin-top: 40px;
}

.is-agent .profile-tile {
  scroll-margin-top: 70px;
}

.profile-tile:empty {
  display: none;
}

.profile-tile > h3 {
  text-transform: none;
  font-size: 1.5rem;
}

.profile-form label {
  /* font-family: 'articulatcf-demibold', 'Lato'; */
  font-weight: 600;
  margin-top: 15px;
}
.profile label {
  /* font-family: 'Lato'; */
  font-weight: 600;
  margin-top: 15px;
  margin-bottom: 2px;
}
.profile .top-label {
  margin-top: 0px;
}
.field-of-fields label {
  /* font-family: 'articulatcf-demibold', 'Lato'; */
  color: var(--coredarkcolor);
  /* color: grey; */
  /* font-size: 0.8rem; */
  font-weight: 600;
  margin-top: 22px;
  margin-bottom: 4px;
}

.field-of-fields label {
  color: var(--labelcolor, var(--coredarkcolor));
}

.field-of-fields .top-label {
  margin-top: 0px;
}

.small-label-help-text {
  display: block;
  margin-bottom: 3px;
}

/* This is the styling for each option in a multi select list */
.form-check label {
  font-weight: 400;
  /* margin-bottom: 3px; */
  margin-top: 2px;
  color: var(--coredarkcolor);
}

/* Classes that get attached to form fields that fail vuelidation */
.invalid label {
  color: red;
}
.invalid small {
  color: red;
}

.social-icon {
  /* margin-top: 8px; */
  margin-right: 2px;
  margin-left: 2px;
  height: 25px;
}

.shaded-label {
  background-color: var(--bootstrapgrey);
  width: 100%;
  padding: 10px;
  border-bottom: 7px solid var(--corelightcolor);
  /* margin-bottom: 100px; */
}
.profile-section {
  margin-left: 10px;
  margin-right: 10px;
}

/* The header/title of each profile section/block */
.section-header {
  text-transform: none;
  font-size: 1.5rem;
}

/* ------------- */

.dashboard-button {
  background-color: var(--thrivehiregrey);
  /* background-color: var(--bootstrapgrey); */
  /* background-color: var(--coredarkcolor); */
  color: var(--corelightcolor);
  /* margin-right: 2px; */
  border-right: 2px solid white;
  padding-top: 6px;
  padding-bottom: 6px;
  cursor: pointer;
  /* font-family: 'articulatcf-demibold', 'Open Sans', 'Lato'; */
  font-weight: 700;
}
.dashboard-button-active {
  background-color: var(--thrivehiregrey);
  background-color: var(--coredarkcolor);
  color: var(--corelightcolor);
  /* margin-right: 2px; */
  /* padding-top: 6px; */
  /* padding-bottom: 6px; */
}

/* -------- RICH TEXT EDITOR --------- */

.editor__content {
  border: 1px solid #ced4da;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  color: #495057;
  font-size: 1rem;
  /* min-height: 100px; */ /* This does not work */
}
/* Get rid of the outline around the text in the tiptap editor */
.ProseMirror:focus {
  outline: none;
}
/* Sets the default minimum height of the rich text editor area */
.ProseMirror {
  min-height: 100px;
}

.editor__content p {
  margin-bottom: 0px;
}

/* ----------------------------------- */

/* -- CALENDAR/DATE PICKER -- */

.vdp-datepicker input {
  border-width: 0px;
  width: 100%; /* Make the inner date picking box fill the entire box */
}
.vdp-datepicker input[type="text"]:focus {
  box-shadow: none;
  border: none;
}

/* ----------------------------------- */

/* This is used for candidate registration, employer registration, password reseting */
/* ------- Bootstrap sign up form ------- */
/* https://getbootstrap.com/docs/4.1/examples/sign-in/ - https://getbootstrap.com/docs/4.1/examples/sign-in/signin.css */

.registration-form {
  height: 100vh;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-align: center;
  align-items: center;
  padding-top: 40px;
  padding-bottom: 40px;
  background-color: #f5f5f5;
}

.form-signup {
  width: 100%;
  max-width: 330px;
  /* min-width: 300px; */
  padding: 15px;
  margin: auto;
}
.form-signup .checkbox {
  font-weight: 400;
}
.form-signup .form-control {
  position: relative;
  box-sizing: border-box;
  height: auto;
  padding: 10px;
  font-size: 16px;
}

.form-control,
.form-control:focus {
  /* font-family: 'articulatcf-regular'; */
  font-size: 16px;
}

.form-control.signing-field {
  display: inline-block;
  width: unset;
  resize: both;
}

/* ---------------------- */

/* https://laracasts.com/discuss/channels/vue/vue-modal-make-it-scrollable-instead-of-background */
/* Restrict the height of modals so that they are fully visible on mobile */
.v--modal-overlay.scrollable .v--modal-box {
  max-height: 90vh;
  overflow-y: auto;
}

.content-block {
  max-width: 650px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: 15px;
}
.content-block img {
  max-width: 100%;
}

/* ------- Styling for informational pages like about us, our team, etc. -------- */
.informational-heading {
  margin-top: 25px;
}
.informational-subheading {
  margin-top: 25px;
}
.informational-text-block {
  margin-bottom: 10px;
}
.informational-list {
  margin-bottom: 10px;
}
/* --------------- */

/* ---- Styling for multiselector ---- */
.multiselect__tag {
  margin-bottom: 0;
  background-color: var(--coredarkcolor);
}
/* ----------------------------------- */

/* --------- LISTINGS ---------- */
.listing-tile {
  background-color: var(--bootstrapgrey);
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 30px;
  padding-right: 30px;
}
/* ------------------------------ */

/* --------- DOCUMENTS ---------- */
.document {
  padding-left: 10%;
  padding-right: 10%;
  padding-top: 3%;
  padding-bottom: 3%;
}
.document p {
  margin-bottom: 10px;
  /* font-size: 1.2rem; */
}
/* ------------------------------ */

.agency-logo-container {
  text-align: center;
  margin-bottom: 50px;
}

.agency-logo-container > img {
  /* width: 20em; */
  /* max-height: 110px; */
  max-width: 400px; /* for super wide logos like gold coast nannies */
}

.mobile-only {
  display: none;
}

/* ------------------- SMALL -------------------- */
@media (max-width: 768px) {
  .mobile-only {
    display: inherit;
  }
  .web-only {
    display: none;
  }

  /* ---- NAVBAR ---- */
  .landing .dropdown-item {
    color: var(--coredarkcolor);
    /* font-family: 'Open Sans'; */
    font-size: 13px;
  }

  .navbar {
    padding-left: 0;
    padding-right: 0;
  }

  /* ------- */

  h1 {
    font-size: 2rem;
    line-height: 1.1;
  }

  /* https://laracasts.com/discuss/channels/vue/vue-modal-make-it-scrollable-instead-of-background */
  /* Restrict the height of modals so that they are fully visible on mobile */
  .v--modal-overlay.scrollable .v--modal-box {
    max-height: 80vh;
    overflow-y: auto;
  }

  .grouped-buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.required-field {
  color: red !important;
  font-weight: 600 !important;
  margin-top: 2px;
}
.required-message {
  color: red;
  font-weight: 600;
  margin-top: 2px;
}

.vld-overlay.is-full-page {
  z-index: 100000;
}

.submit-button-section {
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
}

.text-require-notice {
  font-size: 0.8rem;
  color: var(--coredarkcolor);
}

h3,
h4,
h5 {
  position: relative;
}

.edit-button {
  position: absolute;
  right: 0;
  cursor: pointer;
  width: 24px;
  opacity: 0.8;
  z-index: 1000;
}

.add-button {
  position: absolute;
  right: 0;
  cursor: pointer;
  width: 24px;
  opacity: 1;
}

.add-button > i {
  font-size: 30px;
}

.form-control-file {
  font-size: 0.8rem;
}

.time-picker > input {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.time-picker {
  padding: 0;
  border: none;
}

.description {
  font-size: 80%;
  font-weight: 400;
  margin-bottom: 5px;
  /* margin-top: 10px; */
  white-space: pre-line;
}

.description > div,
.description > span {
  white-space: normal;
}

.one-line-description {
  font-size: 80%;
  font-weight: 400;
  margin-bottom: 5px;
  margin-top: 3px;
}

.admin-description {
  font-size: 80%;
  font-weight: bold;
  margin-bottom: 5px;
  /* margin-top: 10px; */
  white-space: pre-line;
}

.icon-button {
  cursor: pointer;
}

.container-toast.toasted-container.top-right {
  right: 0;
}

.toasted-container {
  z-index: 1000000;
}

.container-toast > .toasted.success-toast.toasted-primary {
  background-color: #47d78a;
  border-radius: 4px;
  padding: 1em;
}

.success-toast > i {
  background-color: white;
  color: #47d78a;
  border-radius: 100%;
  padding: 0.1em;
  margin: 0.2em;
}

.vue-swatches__trigger {
  border: solid 1px grey;
}

.date-picker {
  padding: 0 !important;
}

.date-picker input {
  height: calc(1.5em + 0.75rem) !important;
  border-radius: 4px !important;
  padding-left: 0.75rem;
}

/* Style for form builder */
.field-container,
.item-container {
  display: flex;
  align-items: center;
  margin: 1em;
  flex-wrap: wrap;
}

.field-title,
.item-title {
  flex-basis: 13em;
  flex-shrink: 0;
}

.field-value,
.item-value {
  flex-grow: 1;
  flex-basis: 20em;
  flex-shrink: 0;
}

.space-left {
  flex-basis: 4em;
  display: flex;
}

.space-right {
  flex-basis: 2em;
  text-align: center;
}

.field-value,
.item-value {
  display: flex;
  align-items: center;
  padding-left: 2em;
}

.expand-button,
.collapse-button {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-basis: 1.8em;
}

.delete-button {
  cursor: pointer;
  color: red;
}

.option-list-container {
  width: 100%;
}

.option-list-container > .multiselect .multiselect__tag {
  white-space: pre-wrap;
}

.button-group-container {
  display: flex;
}

.cancel-btn {
  flex-grow: 1;
}

.multiselect-option {
  display: flex;
  align-items: center;
}

.multiselect-icon {
  flex-basis: 20px;
}

.multiselect-icon > .material-icons {
  width: 30px;
}

.collapse-expand-button-container {
  display: flex;
}

.expand-btn {
  margin-right: 1em;
  margin-bottom: 1em;
}

.field-type-icon > .material-icons {
  width: 30px;
}

.edit-form-icon {
  cursor: pointer;
}

li .invalid-option {
  color: red;
  opacity: 0.5;
}
/* End of style for form builder */

.drag-drop-item {
  cursor: move; /* fallback if grab cursor is unsupported */
  cursor: grab;
  cursor: -moz-grab;
  cursor: -webkit-grab;
}

.drag-drop-item *:not(button) {
  cursor: move; /* fallback if grab cursor is unsupported */
  cursor: grab;
  cursor: -moz-grab;
  cursor: -webkit-grab;
}

.drag-drop-item:active {
  cursor: grabbing;
  cursor: -moz-grabbing;
  cursor: -webkit-grabbing;
}

.drag-drop-item *:not(button):active {
  cursor: grabbing;
  cursor: -moz-grabbing;
  cursor: -webkit-grabbing;
}

.booking-detail label {
  margin-top: 2px !important;
}

.booking-detail .description {
  display: none;
}

.booking-detail .admin-description {
  display: none;
}

.calendar .cv-event {
  white-space: normal;
}

.calendar .cv-event:not(.unclickable) {
  cursor: pointer;
}

.theme-default .cv-event.continued::before,
.theme-default .cv-event.toBeContinued::after {
  color: #000;
}

.theme-default .cv-day.past {
  opacity: 0.7;
}

/* Hide remark in case of public view */
.profile-view-only .description {
  display: none;
}

.info-key {
  font-weight: bold;
  margin-right: 0.5em;
  color: var(--coredarkcolor);
}

.inherit-color-section h3 {
  color: inherit;
}

.inherit-color-section label {
  color: inherit;
}

.inherit-color-section .info-key {
  color: inherit;
}

.inherit-color-section .dialog-container h3 {
  color: var(--coredarkcolor);
}

.inherit-color-section .dialog-container label {
  color: var(--coredarkcolor);
}

.inherit-color-section .dialog-container .info-key {
  color: var(--coredarkcolor);
}

.inherit-color-link {
  color: inherit;
}

/* Things to hide when embedding as an iframe - in particular for application forms */
.in-embed .agency-logo-container {
  display: none;
}
.in-embed .agency-owner {
  display: none;
}
.in-embed nav {
  display: none;
}

.in-embed {
  margin: 0;
}

/* Custom grid layout */
.col-lg-custom-24,
.col-lg-custom-48,
.col-lg-custom-72 {
  position: relative;
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
}

.header-calendar .cv-weeks {
  display: none;
}

@media (min-width: 992px) {
  .col-lg-custom-24 {
    flex: 0 0 20%;
    max-width: 20%;
  }

  .col-lg-custom-48 {
    flex: 0 0 40%;
    max-width: 40%;
  }

  .col-lg-custom-72 {
    flex: 0 0 60%;
    max-width: 60%;
  }
}

/* Style for customize checkbox */
/* The container */
.field-of-fields label.checkbox-container,
label.checkbox-container {
  display: block;
  position: relative;
  padding-left: 35px;
  margin-bottom: 0;
  cursor: pointer;
  font-weight: normal;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  margin-top: 0;
}

/* Hide the browser's default checkbox */
.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 25px;
  width: 25px;
  background-color: #eee;
  border: solid 2px #2196f3;
}

/* On mouse-over, add a grey background color */
.checkbox-container:hover input ~ .checkmark {
  background-color: #ccc;
}

/* When the checkbox is checked, add a blue background */
.checkbox-container input:checked ~ .checkmark {
  background-color: #2196f3;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.checkbox-container .checkmark:after {
  left: 8px;
  top: 3px;
  width: 8px;
  height: 14px;
  border: solid white;
  border-width: 0 4px 4px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
/* End of style for customize checkbox */

.email-textbox > input {
  text-transform: lowercase;
}

.delete-icon {
  color: red;
  cursor: pointer;
}

.label-no-margin-top label {
  margin-top: 0;
}

.label-no-margin-top
  .field-of-fields.input-dialog-container
  label:not(.checkbox-label) {
  margin-top: 22px;
}

.label-small-margin-top label {
  margin-top: 0.5em;
}

.form-check-input:checked + label .highlight-selected {
  font-weight: bold;
}

.vue-tooltip-visible {
  z-index: 20000;
}

.align-left {
  text-align: left;
}

.align-center {
  text-align: center;
}

.vdp-datepicker .vdp-datepicker__clear-button {
  font-weight: bold;
  position: absolute;
  right: 8px;
}

.broadcast-dialog .data-table {
  max-height: 500px;
  overflow: auto;
  display: block;
}

.blaster-email-component .data-table {
  max-height: 500px;
  overflow: auto;
  display: block;
}

button:disabled,
button[disabled] {
  opacity: 0.5;
}

.inline-edit-icon {
  cursor: pointer;
}

.day-month-picker .mx-input-wrapper {
  height: 100%;
}

.day-month-picker .mx-input-wrapper > div {
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: 0.5em;
}

.day-month-picker-popup .mx-calendar-header {
  display: none;
}

.day-month-picker-popup .mx-table-date thead {
  display: none;
}

.form-control.mx-datepicker {
  display: block;
  width: 100%;
}

.form-control.mx-datepicker input {
  border: none;
}

.mx-datepicker-popup {
  z-index: 20000;
}

.submit-clicked .is-required:invalid {
  outline: solid red 2px;
}

.login-page {
  background-color: #f5f5f5;
}

.login-page .footer-panel {
  display: none;
}

.application-form-container {
  width: 75%;
}

h3.dialog-title {
  display: flex;
  flex-wrap: wrap;
}

h3.dialog-title span:first-child {
  padding-right: 0.25em;
}

.checkbox-option-profile-builder + div > label:first-child {
  margin-top: 10px;
}

.button-link {
  color: #007bff !important;
  cursor: pointer;
}

.inline-answer {
  display: inline;
}

.inline-answer > p {
  display: inline-flex;
}

/* Enginehow */
.enginehow-list-item {
  margin-top: 4px;
}

.other-buttons > span:not(:last-child)::after {
  content: "|";
  color: var(--coredarkcolor);
}

.view-only-access .edit-access-buttons {
  display: none;
}

.hide-logo .logo {
  display: none;
}

.copy-button {
  margin-left: 0.5em;
  cursor: pointer;
}

.copy-button:active {
  color: #007bff;
}

.timepicker-container .vue__time-picker .dropdown {
  height: 27em;
}

.timepicker-container .vue__time-picker .dropdown .select-list {
  height: 27em;
}

.section-template-container ul {
  display: inline-block;
}

@media (max-width: 992px) {
  .dialog-container > .p-5 {
    padding: 2em !important;
  }

  .dialog-content-container > .p-5 {
    padding: 2em !important;
  }

  .top-level-div {
    padding-left: 2em;
    padding-right: 2em;
  }

  .calendar .cv-event {
    font-size: 0.9em;
  }

  .calendar .cv-day-number {
    font-size: 0.9em;
  }

  .application-form-container {
    width: 80%;
  }
}

.form-label-btn {
  display: flex;
}

.form-label-btn > i {
  cursor: pointer;
}

@media (min-width: 769px) {
  .col-md-custom-24 {
    flex: 0 0 20%;
    max-width: 20%;
  }
}

@media (max-width: 768px) {
  .dialog-container > .p-5 {
    padding: 1.5em !important;
  }
  .dialog-content-container > .p-5 {
    padding: 1.5em !important;
  }

  .top-level-div {
    padding-left: 1em;
    padding-right: 1em;
  }

  .calendar .cv-event {
    font-size: 0.8em;
  }

  .calendar .cv-day-number {
    font-size: 0.85em;
  }

  .calendar .cv-header .periodLabel {
    font-size: 1.2em;
  }

  .application-form-container {
    width: 85%;
  }

  h3.dialog-title {
    font-size: 1.5em;
  }

  .hide-editor-button-mobile .text-editor-component > .button-containers {
    display: none;
  }

  h4.mobile-smaller {
    font-size: 1.3em;
  }
}

@media (max-width: 600px) {
  .dialog-container > .p-5 {
    padding: 1.2em 1em !important;
  }

  .dialog-content-container > .p-5 {
    padding: 1.2em 1em !important;
  }

  .top-level-div {
    padding-left: 0.5em;
    padding-right: 0.5em;
  }

  .row > .calendar {
    padding: 0 4px;
  }

  .calendar .cv-event {
    font-size: 0.7em;
  }

  .calendar .cv-day-number {
    font-size: 0.75em;
  }

  .application-form-container {
    width: 90%;
  }

  .row .timepicker-container .vue__time-picker {
    width: 9em;
  }

  .row .timepicker-container .vue__time-picker input.display-time {
    width: 9em;
  }

  h3.dialog-title {
    font-size: 1.35em;
  }

  .mylisting-button {
    padding: 12px 15px;
  }
}

@media (max-width: 400px) {
  .row .timepicker-container .vue__time-picker {
    width: 6em;
  }
  .row .timepicker-container .vue__time-picker input.display-time {
    width: 6em;
  }

  h3.dialog-title {
    font-size: 1.2em;
  }

  h4.description {
    font-size: 1.1em;
  }

  h5.sub-title {
    font-size: 1.08em;
  }
}
.enginehow-tooltip {
  left: -28px !important;
}
.cropper-crop-box,
.cropper-view-box {
  border-radius: 50%;
}

.cropper-view-box {
  box-shadow: 0 0 0 1px #39f;
  outline: 0;
}
.cursor-pointer {
  cursor: pointer;
}
.section-body .section-template-container .display-content font {
  line-height: 19.2px;
}
#application-form-font-color h2,
#application-form-font-color h3,
#application-form-font-color h4,
#application-form-font-color label,
#application-form-font-color .item-option,
#application-form-font-color .item-label,
#application-form-font-color .list-item,
#application-form-font-color .description,
#application-form-font-color div b,
#application-form-font-color .step-status-text,
#application-form-font-color .step-status-text {
  color: var(--appFormFontColor) !important;
}

.enginehire-help {
  position: fixed;
  bottom: 4px;
  right: 4px;
  z-index: 100000;
}

.enginehire-help > i {
  font-size: 40px;
}

.display-flex {
  display: flex;
}

.display-block {
  display: block;
}

.display-none {
  display: none;
}

.clear-button {
  display: block;
  cursor: pointer;
}

.item-label.header-label {
  padding-left: 0.5em;
}

.close-toast-btn {
  color: white !important;
}

.distance-remark {
  font-style: italic;
}

.space-item {
  flex-grow: 1;
}

.button-loading {
  border: 12px solid #f3f3f3; /* Light grey */
  border-top: 12px solid var(--coredarkcolor);
  border-radius: 50%;
  width: 44px;
  height: 44px;
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.button-done {
  font-size: 44px;
  color: var(--coredarkcolor);
}

.transparent-btn {
  display: flex;
  padding-top: 0;
  padding-bottom: 0;
  border: none;
  background: none;
  outline: none !important;
  cursor: default !important;
}
.broadcast-dialog .data-table .table .table-sticky-header {
  top: -2px !important;
}

.topic-description img {
  max-width: 100%;
}

h2.mobile-smaller {
  font-size: 3em;
}
@media (max-width: 600px) {
  h2.mobile-smaller {
    font-size: 2em;
  }
}

.refresh-button {
  cursor: pointer;
}

.menu-bar-panel.sticky-summary {
  top: 88px !important;
}

@media (max-width: 768px) {
  .menu-bar-panel.sticky-summary {
    top: 66px !important;
  }
}

.overflow-x-auto {
  overflow-x: auto;
}

.start-date-filter {
  padding-right: 0.5em;
  flex-grow: 1;
}

.end-date-filter {
  padding-right: 0.5em;
  flex-grow: 1;
}

.spacing {
  flex-grow: 1;
}

.full-width {
  width: 100%;
}

.visible-icon {
  color: #adadad;
  cursor: pointer;
}

.edit-builder-icon {
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 18px;
}

.add-option-icon {
  font-size: 12px;
}

.checkbox-column-header.inline-content {
  display: none;
}

.checkbox-column-content.inline-content {
  display: inline;
}

.checkbox-column-header.inline-content:after {
  content: ": ";
}

.checkbox-column-content.inline-content:not(:last-child):after {
  content: ", ";
}
.checkbox-column-content.inline-content:last-child:after {
  content: ".";
}

.document-container.submit-clicked .is-required.StripeElement--empty::after {
  content: "This field is required";
  display: block;
  color: red;
  font-weight: bold;
  margin-top: 0.5rem;
}

.tooltip {
  display: block !important;
  z-index: 10000;
}
.tooltip .tooltip-inner {
  background: black;
  color: white;
  border-radius: 16px;
  padding: 5px 10px 4px;
}
.tooltip .tooltip-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  margin: 5px;
  border-color: black;
  z-index: 1;
}
.tooltip[x-placement^="top"] {
  margin-bottom: 5px;
}
.tooltip[x-placement^="top"] .tooltip-arrow {
  border-width: 5px 5px 0 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  bottom: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}
.tooltip[x-placement^="bottom"] {
  margin-top: 5px;
}
.tooltip[x-placement^="bottom"] .tooltip-arrow {
  border-width: 0 5px 5px 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
  top: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}
.tooltip[x-placement^="right"] {
  margin-left: 5px;
}
.tooltip[x-placement^="right"] .tooltip-arrow {
  border-width: 5px 5px 5px 0;
  border-left-color: transparent !important;
  border-top-color: transparent !important;
  border-bottom-color: transparent !important;
  left: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}
.tooltip[x-placement^="left"] {
  margin-right: 5px;
}
.tooltip[x-placement^="left"] .tooltip-arrow {
  border-width: 5px 0 5px 5px;
  border-top-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  right: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}
.tooltip.popover .popover-inner {
  background: #f9f9f9;
  color: black;
  padding: 24px;
  border-radius: 5px;
  box-shadow: 0 5px 30px rgba(black, 0.1);
}
.tooltip.popover .popover-arrow {
  border-color: #f9f9f9;
}
.tooltip[aria-hidden="true"] {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.15s, visibility 0.15s;
}
.tooltip[aria-hidden="false"] {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.15s;
}
</style>


// WEBPACK FOOTER //
// src/App.vue